import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  AlertCircle, 
  Info, 
  Plus, 
  Building2, 
  ExternalLink
} from "lucide-react";
import { cn } from "@/lib/utils";

export function DocsPage() {
  const [activeSection, setActiveSection] = useState("schedule-types");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -35% 0px" }
    );

    document.querySelectorAll("section[id]").forEach((section) => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };

  const sections = [
    { id: "schedule-types", title: "Schedule Types" },
    { id: "config-rules", title: "Config Rules" },
    { id: "unsupported", title: "Unsupported" },
    { id: "new-school", title: "New School" },
    { id: "our-mission", title: "Our Mission" },
  ];

  return (
    <div className="relative">
      {/* Timeline Navigation - Right Side */}
      <aside className="hidden xl:block fixed right-8 top-32 w-56">
        <div className="space-y-6">
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
              On This Page
            </p>
            <nav className="relative">
              {/* Timeline line */}
              <div className="absolute left-[7px] top-3 bottom-3 w-[2px] bg-gradient-to-b from-border via-border to-transparent" />
              
              <div className="space-y-1">
                {sections.map((section, index) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={cn(
                      "group relative w-full text-left text-sm transition-all duration-200 pl-8 py-2 rounded-md hover:bg-muted/50",
                      activeSection === section.id
                        ? "text-foreground font-medium"
                        : "text-muted-foreground"
                    )}
                  >
                    {/* Indicator dot */}
                    <span
                      className={cn(
                        "absolute left-0 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full transition-all duration-200",
                        activeSection === section.id
                          ? "bg-primary"
                          : "bg-muted border-2 border-border group-hover:border-primary/50 group-hover:bg-primary/10"
                      )}
                    />
                    <span className={cn(
                      "transition-all duration-200",
                      activeSection === section.id && "translate-x-0.5"
                    )}>
                      {section.title}
                    </span>
                  </button>
                ))}
              </div>
            </nav>
          </div>

          {/* Quick Links */}
          <div className="space-y-2 pt-6 border-t">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Quick Links
            </p>
            <Link to="/dashboard/schools/new">
              <Button variant="ghost" size="sm" className="w-full justify-start text-xs h-9 hover:bg-muted">
                <Plus className="mr-2 h-3.5 w-3.5" />
                Create School
              </Button>
            </Link>
            <Link to="/dashboard/map">
              <Button variant="ghost" size="sm" className="w-full justify-start text-xs h-9 hover:bg-muted">
                <Building2 className="mr-2 h-3.5 w-3.5" />
                Browse Schools
              </Button>
            </Link>
            <a href="https://nces.ed.gov/ccd/schoolsearch/" target="_blank" rel="noopener noreferrer">
              <Button variant="ghost" size="sm" className="w-full justify-start text-xs h-9 hover:bg-muted">
                <ExternalLink className="mr-2 h-3.5 w-3.5" />
                NCES Database
              </Button>
            </a>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-6 md:px-8 py-12 space-y-16">
        {/* Header */}
        <header className="space-y-3 pb-8 border-b">
          <h1 className="text-4xl font-bold tracking-tight">Documentation</h1>
          <p className="text-lg text-muted-foreground">
            Complete guide for managing schedules and schools on Saturn Dashboard
          </p>
        </header>

        {/* Schedule Types Section */}
        <section id="schedule-types" className="scroll-mt-24 space-y-6">
          <div className="space-y-2">
            <h2 className="text-3xl font-bold tracking-tight">Schedule Types</h2>
            <p className="text-muted-foreground">
              High school schedules generally fall into 6 different categories
            </p>
          </div>

          <div className="space-y-6">
            {[
              {
                type: "SED",
                name: "Same Every Day",
                description: "Students take the same classes in the same order every day with no variation. This is the simplest of our six schedule types.",
                rule: "1 day type, 1 core schedule, 1 day in rotation pattern"
              },
              {
                type: "SED+",
                name: "Same Every Day Plus",
                description: "Classes are in the same order every day, but bell times may change between days. Times can be different on one day or all 5, or class order is different on one day of the week. The + day should be locked to a specific day of the week.",
                rule: "1 day type, 2 core schedules, 1 day in rotation"
              },
              {
                type: "A/B",
                name: "A/B Schedule",
                description: "Students have two different types of days that typically alternate one after the other. Day types can have different names like '1' and '2', 'RED' and 'WHITE', or 'A' and 'B'.",
                rule: "2 day types, 2 core schedules, 2 days in rotation pattern"
              },
              {
                type: "A/B+",
                name: "A/B Plus",
                description: "An extended version of A/B with one additional day type included. The + day typically occurs on a specific day (Mon/Wed/Fri) while A/B days rotate independently. The + day should either be a schedule with a locked day, or its own day type.",
                rule: "2-3 day types, 2-4 core schedules, 2, 5, or 10 days in rotation"
              },
              {
                type: "M-F",
                name: "Monday through Friday",
                description: "Every week is the exact same, though each day of the week has different schedules. Each day has its own unique schedule that repeats weekly (frozen 5-day rotation). A true M-F will have 5 core schedules.",
                rule: "5 day types, 5-20 days in rotation pattern"
              },
              {
                type: "MDR",
                name: "Multiple Day Rotating",
                description: "Extended version of A/B with more than two day types (e.g., A, B, C, D). Day types can be numerical, alphabetic, or school colors and rotate continuously. Typically all the same (3 types, 3 schedules, 3 days).",
                rule: "Multiple day types, multiple core schedules, multiple days in rotation"
              }
            ].map((schedule) => (
              <div key={schedule.type} className="space-y-2">
                <div className="flex items-baseline gap-3">
                  <code className="text-sm font-semibold px-2 py-0.5 bg-muted/50 rounded">{schedule.type}</code>
                  <h3 className="text-lg font-semibold">{schedule.name}</h3>
                </div>
                <p className="text-sm leading-relaxed">{schedule.description}</p>
                <p className="text-xs text-muted-foreground font-medium">{schedule.rule}</p>
              </div>
            ))}
          </div>
        </section>

        <hr className="border-t" />

        {/* Config Rules Section */}
        <section id="config-rules" className="scroll-mt-24 space-y-6">
          <div className="space-y-2">
            <h2 className="text-3xl font-bold tracking-tight">Config Rules & Checklist</h2>
            <p className="text-muted-foreground">
              Configuration rules and guidelines for each schedule type
            </p>
          </div>

          <div className="bg-muted/50 border p-4 space-y-1 rounded-lg">
            <p className="text-sm font-semibold flex items-center gap-2">
              <Info className="h-4 w-4" />
              What does "config" or "reconfig" mean?
            </p>
            <p className="text-sm text-muted-foreground">
              This refers to the way a school is configured - meaning the school's settings, schedules, name/mascot, calendar, etc. 
              When one thing needs to be reconfigured, double check if other parts should be updated.
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Config Checklist</h3>
            <p className="text-sm text-muted-foreground">Check ALL of these when making ANY configuration change</p>
            <ul className="space-y-1.5 text-sm">
              {[
                "Schedule type correct?",
                "Day types correct?",
                "Lunch type correct?",
                "Rotation pattern correct?",
                "Does the rotation pattern need to be updated?",
                "Has the calendar been rebuilt using the updated information?",
                "Are there any missing schedules on the calendar?",
                "Double check what schedules should be in ACF or not",
                "Based on submission, Google the school to find updated schedule if needed"
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-muted-foreground">{index + 1}.</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-muted/50 border p-4 space-y-2 rounded-lg">
            <p className="text-sm font-semibold flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Important Rules
            </p>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              <li>M-F schedules should <strong>never</strong> have locked days</li>
              <li>Every Day Type needs an associated schedule in ACF</li>
            </ul>
          </div>
        </section>

        <hr className="border-t" />

        {/* Unsupported Schedules Section */}
        <section id="unsupported" className="scroll-mt-24 space-y-6">
          <div className="space-y-2">
            <h2 className="text-3xl font-bold tracking-tight">Schedules We Cannot Support</h2>
            <p className="text-muted-foreground">
              Common scenarios where we're unable to support a school's schedule
            </p>
          </div>

          <div className="space-y-6">
            {[
              {
                title: "Overlapping Periods",
                description: "When different grades have lunch at different times, resulting in overlapping periods.",
                examples: [
                  "9/10th grade have lunch at different times from 11/12th",
                  "Periods 4 and 5 overlap, making it impossible to have correct times"
                ]
              },
              {
                title: "Multiple Versions of Bell Schedule",
                description: "Different schedules for different grades.",
                examples: [
                  "9th grade vs. 10-12th grade have different bell schedules",
                  "Lower vs. upperclassmen schedules can't be reconciled"
                ]
              },
              {
                title: "Split Campuses",
                description: "School has multiple buildings/campuses with different schedules.",
                examples: [
                  "If we have enough info for both campuses, we could create 2 separate schools"
                ]
              },
              {
                title: "K-12 or Middle School",
                description: "We don't support middle schools or K-12 combinations.",
                examples: [
                  "We don't support middle schools",
                  "May support just 9-12 at K-12 schools if info is available"
                ]
              }
            ].map((item, index) => (
              <div key={index} className="space-y-2">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
                <ul className="space-y-1 text-sm">
                  {item.examples.map((example, exIndex) => (
                    <li key={exIndex} className="flex items-start gap-2 pl-2">
                      <span className="text-muted-foreground">•</span>
                      <span>{example}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <hr className="border-t" />

        {/* Creating New Schools Section */}
        <section id="new-school" className="scroll-mt-24 space-y-6">
          <div className="space-y-2">
            <h2 className="text-3xl font-bold tracking-tight">Creating New Schools</h2>
            <p className="text-muted-foreground">
              Step-by-step guide for adding new schools to Saturn
            </p>
          </div>

          <div className="bg-muted/50 border p-4 space-y-2 rounded-lg">
            <p className="text-sm font-semibold">When to Create New Schools</p>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              <li>Students requesting their school via support</li>
              <li>Creation of a freshman campus</li>
              <li>Other special circumstances</li>
            </ul>
          </div>

          <div className="flex gap-3">
            <Link to="/dashboard/schools/new">
              <Button className="gap-2">
                <Plus className="h-4 w-4" />
                Create School Now
              </Button>
            </Link>
            <Link to="/dashboard/map">
              <Button variant="outline" className="gap-2">
                <Building2 className="h-4 w-4" />
                Browse Schools
              </Button>
            </Link>
          </div>

          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold">Section 1: Basic Information</h3>
              <p className="text-sm text-muted-foreground">Core school details, identification, and email domains</p>
              <div className="space-y-4">
                {[
                  { 
                    field: "School ID", 
                    format: "schoolname_state",
                    desc: "Unique identifier (lowercase, underscores only)",
                    example: "stanford_university",
                    required: true
                  },
                  { field: "School Title", desc: "Full name of the school", example: "Stanford University", required: true },
                  { field: "Nickname", desc: "Optional school nickname or colloquial name" },
                  { field: "Website", desc: "School's website URL if available" },
                  { field: "NCES Code", desc: "Optional for US schools, not required for Canadian schools", link: "https://nces.ed.gov/ccd/schoolsearch/" },
                  { field: "Enrollment", desc: "Number of students (optional)" },
                  { field: "School Status", desc: "Set to 'live' or 'waitlist' based on readiness", required: true },
                  { field: "Lite Level", desc: "Shown only if status is not 'live'" },
                  { field: "Email Domains", desc: "Add school email domains (e.g., stanford.edu) - optional" }
                ].map((item, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{item.field}</span>
                      {item.required && (
                        <span className="text-xs text-red-600 dark:text-red-400 font-medium">*required</span>
                      )}
                    </div>
                    {item.format && (
                      <code className="text-xs bg-muted/50 px-2 py-0.5 rounded">{item.format}</code>
                    )}
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                    {item.example && (
                      <p className="text-xs text-muted-foreground italic">Example: {item.example}</p>
                    )}
                    {item.link && (
                      <a href={item.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                        NCES Search <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold">Section 2: Location Information</h3>
              <p className="text-sm text-muted-foreground">Geographic location and timezone. Start by typing an address to auto-populate location fields (country, state, latitude, longitude, and timezone).</p>
              <div className="space-y-3 text-sm">
                {[
                  { field: "Country", desc: "United States or Canada" },
                  { field: "State/Province", desc: "Will show states for US, provinces for Canada" },
                  { field: "Latitude", desc: "Auto-populated from address lookup" },
                  { field: "Longitude", desc: "Auto-populated from address lookup" },
                  { field: "Timezone", desc: "Auto-populated from address lookup", required: true }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <span className="font-semibold min-w-[120px]">{item.field}:</span>
                    <div className="flex-1">
                      <span className="text-muted-foreground">{item.desc}</span>
                      {item.required && (
                        <span className="text-xs text-red-600 dark:text-red-400 font-medium ml-2">*required</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold">Section 3: General Settings & Appearance</h3>
              <p className="text-sm text-muted-foreground">Privacy settings and visual customization</p>
              <div className="space-y-3 text-sm">
                {[
                  { field: "Private School", desc: "Toggle if this is a private school" },
                  { field: "School Logo", desc: "Upload school logo (PNG or JPEG)" },
                  { field: "Primary Color", desc: "Select a color that fits the school's branding" }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <span className="font-semibold min-w-[120px]">{item.field}:</span>
                    <span className="text-muted-foreground flex-1">{item.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <hr className="border-t" />

        {/* Our Mission Section */}
        <section id="our-mission" className="scroll-mt-24 space-y-6">
          <div className="space-y-2">
            <h2 className="text-3xl font-bold tracking-tight">What We're Building</h2>
            <p className="text-muted-foreground">
              Our vision for Saturn and what we're trying to achieve
            </p>
          </div>

          <div className="space-y-4">
            <p className="text-base leading-relaxed">
              Saturn is building <strong>the most fun calendar for everyone.</strong> Whether you're in high school, college, or just someone new to digital calendars looking for a better way to make plans — Saturn is here to make scheduling effortless and enjoyable.
            </p>

            <p className="text-base leading-relaxed">
              We started with high school schedules because they're incredibly complex, but our ambition extends far beyond that. We're creating a calendar experience that makes planning your day feel less like a chore and more like a seamless part of your life.
            </p>

            <p className="text-base leading-relaxed">
              When you're configuring schools and schedules, remember: real people depend on this information every day. The work you're doing helps students get to class on time, make plans with friends, and navigate their lives with confidence.
            </p>

            <p className="text-base leading-relaxed text-muted-foreground italic">
              Every school we add and every feature we build is a step toward making calendars more accessible, accurate, and enjoyable for everyone. Let's build something people love.
            </p>
          </div>
        </section>

        {/* Footer spacing */}
        <div className="h-16" />
      </div>
    </div>
  );
}
